import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, filterParams, filterKey } from "@/lib/api";
import { fmtInt, fmtCost, fmtTime } from "@/lib/format";
import { ChevronRight, ChevronDown } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const LIMIT_OPTIONS = [10, 25, 50, 100, 150];

function KindBadge({ kind, name }) {
  const isLog = kind === "log";
  return (
    <span
      className="inline-block px-2 py-0.5 text-[11px] font-mono border"
      style={
        isLog
          ? { color: "#F59E0B", borderColor: "rgba(245,158,11,0.3)", background: "rgba(245,158,11,0.08)" }
          : { color: "#3B82F6", borderColor: "rgba(59,130,246,0.3)", background: "rgba(59,130,246,0.08)" }
      }
    >
      {name || kind}
    </span>
  );
}

export default function EventsTable({ filters }) {
  const [expanded, setExpanded] = useState(null);
  const [limit, setLimit] = useState(10);
  const { data = [] } = useQuery({
    queryKey: ["events", limit, ...filterKey(filters)],
    queryFn: async () => (await api.get("/events", { params: { ...filterParams(filters), limit } })).data,
    refetchInterval: 20000,
  });

  const toggle = (id) => setExpanded((e) => (e === id ? null : id));

  return (
    <div className="bg-card border border-border" data-testid="events-table">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="overline">raw events · debug</p>
          <h3 className="text-sm font-medium mt-1">Metrics &amp; log records — expand for attributes</h3>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono text-xs text-muted-foreground">showing {data.length}</span>
          <Select value={String(limit)} onValueChange={(v) => setLimit(Number(v))}>
            <SelectTrigger className="h-8 w-[130px] rounded-none bg-black/50 border-border font-mono text-xs focus:ring-1 focus:ring-white" data-testid="events-limit-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none font-mono">
              {LIMIT_OPTIONS.map((n) => <SelectItem key={n} value={String(n)}>last {n}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full font-mono text-xs">
          <thead>
            <tr className="text-muted-foreground border-b border-border">
              {["", "Timestamp", "Event / Metric", "Session", "User", "Model", "Token", "In", "Out", "Cache", "Cost", "Dur"].map((h, i) => (
                <th key={i} className="text-left font-normal px-3 py-2 whitespace-nowrap overline">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 && (
              <tr><td colSpan={12} className="px-3 py-8 text-center text-muted-foreground">no events in range</td></tr>
            )}
            {data.map((e) => (
              <React.Fragment key={e.id}>
                <tr
                  onClick={() => toggle(e.id)}
                  className="border-b border-border/50 hover:bg-white/[0.03] transition-colors cursor-pointer"
                  data-testid={`event-row-${e.id}`}
                >
                  <td className="px-3 py-2.5 text-muted-foreground">
                    {expanded === e.id ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  </td>
                  <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{fmtTime(e.timestamp)}</td>
                  <td className="px-3 py-2.5"><KindBadge kind={e.kind} name={e.name} /></td>
                  <td className="px-3 py-2.5 text-muted-foreground max-w-[180px] truncate">{e.session_id || "—"}</td>
                  <td className="px-3 py-2.5 text-muted-foreground">{e.user_id || "—"}</td>
                  <td className="px-3 py-2.5 text-muted-foreground">{e.model || "—"}</td>
                  <td className="px-3 py-2.5 text-muted-foreground max-w-[130px] truncate" data-testid={`event-token-${e.id}`}>{e.source_token_label || "—"}</td>
                  <td className="px-3 py-2.5 text-blue-400 tabular-nums">{e.input_tokens != null ? fmtInt(e.input_tokens) : (e.token_subtype === "input" ? fmtInt(e.value) : "—")}</td>
                  <td className="px-3 py-2.5 text-amber-400 tabular-nums">{e.output_tokens != null ? fmtInt(e.output_tokens) : (e.token_subtype === "output" ? fmtInt(e.value) : "—")}</td>
                  <td className="px-3 py-2.5 text-zinc-400 tabular-nums">{e.cache_tokens != null ? fmtInt(e.cache_tokens) : (e.token_subtype === "cache" ? fmtInt(e.value) : "—")}</td>
                  <td className="px-3 py-2.5 text-white tabular-nums">{e.cost != null ? fmtCost(e.cost) : "—"}</td>
                  <td className="px-3 py-2.5 text-muted-foreground tabular-nums">{e.duration_ms != null ? `${fmtInt(e.duration_ms)}ms` : "—"}</td>
                </tr>
                {expanded === e.id && (
                  <tr className="bg-black/40" data-testid={`event-detail-${e.id}`}>
                    <td colSpan={12} className="px-4 py-3">
                      {e.body != null && (
                        <div className="mb-3">
                          <p className="overline mb-1">body</p>
                          <pre className="text-[11px] text-zinc-300 whitespace-pre-wrap break-all bg-black border border-border p-3">{typeof e.body === "string" ? e.body : JSON.stringify(e.body, null, 2)}</pre>
                        </div>
                      )}
                      <p className="overline mb-1">attributes</p>
                      <pre className="text-[11px] text-zinc-300 whitespace-pre-wrap break-all bg-black border border-border p-3">{JSON.stringify(e.attributes || {}, null, 2)}</pre>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
